import { api, Pagination } from './api';
export enum AdminRole {
  SUPER_ADMIN = 100,
  ADMIN = 50,
  NONE = 0,
}

export const AdminRoleLabel = {
  [AdminRole.SUPER_ADMIN]: 'Super Admin',
  [AdminRole.ADMIN]: 'Admin',
};

export enum OnboardingStep {
  START = 0,
  READY_FOR_INTERVIEW = 10,
  INTERVIEW = 20,
  UPLOAD_PHOTO = 30,
  PLAN_COMPARISON = 40,
  SUBSCRIPTION = 50,
  REVIEW_PROFILE = 60,
  COMPLETED = -10,
}

export enum AccountStatus {
  ACTIVE = 0,
  BLOCKED = 1,
  PAUSED = 2,
  SKIP_MATCH = 3,
}

export const AccountStatusLabel: Record<AccountStatus, string> = {
  [AccountStatus.ACTIVE]: 'Active',
  [AccountStatus.BLOCKED]: 'Blocked',
  [AccountStatus.PAUSED]: 'Paused',
  [AccountStatus.SKIP_MATCH]: 'Skip Match',
};

export enum MembershipType {
  FREEMIUM = 0,
  PREMIUM_TRIAL = 1,
  PREMIUM = 2,
}

export const MembershipTypeLabel: Record<MembershipType, string> = {
  [MembershipType.FREEMIUM]: 'Freemium',
  [MembershipType.PREMIUM_TRIAL]: 'Premium Trial',
  [MembershipType.PREMIUM]: 'Premium',
};

export enum UserType {
  NORMAL = 0,
  SAMPLE = 1,
  TEST = 2,
}

export const UserTypeLabel: Record<UserType, string> = {
  [UserType.NORMAL]: 'Normal',
  [UserType.SAMPLE]: 'Sample',
  [UserType.TEST]: 'Test',
};

export enum ProfileGender {
  FEMALE = 2,
  MALE = 4,
  NON_BINARY = 8,
}

export const ProfileGenderLabel: Record<ProfileGender, string> = {
  [ProfileGender.FEMALE]: 'Female',
  [ProfileGender.MALE]: 'Male',
  [ProfileGender.NON_BINARY]: 'Non-binary',
};

export enum INTERVIEW_TOPIC {
  INIT = 'init',
  LIFE_STYLE = 'life_style',
  FAMILY = 'family',
  RELATIONSHIP = 'relationship',
}

export const INTERVIEW_TOPIC_LABEL: Record<INTERVIEW_TOPIC, string> = {
  [INTERVIEW_TOPIC.INIT]: 'Init',
  [INTERVIEW_TOPIC.LIFE_STYLE]: 'Life Style',
  [INTERVIEW_TOPIC.FAMILY]: 'Family',
  [INTERVIEW_TOPIC.RELATIONSHIP]: 'Relationship',
};
export const HIDEABLE_PROFILE_FIELDS = [
  'age',
  'height',
  'zodiac',
  'chineseZodiac',
  'homeTown',
  'education',
  'occupation',
  'maritalStatus',
  'haveChildren',
  'ethnicity',
  'languages',
  'religion',
  'residentStatus',
  'smoking',
  'drinking',
  'drug',
  'summary',
] as const;

export type HideableProfileField = (typeof HIDEABLE_PROFILE_FIELDS)[number];

export const HIDEABLE_PROFILE_FIELD_LABEL: Record<
  HideableProfileField,
  string
> = {
  age: 'Age',
  height: 'Height',
  zodiac: 'Zodiac',
  chineseZodiac: 'Chinese zodiac',
  homeTown: 'Hometown',
  education: 'Education',
  occupation: 'Occupation',
  maritalStatus: 'Marital status',
  haveChildren: 'Has children',
  ethnicity: 'Ethnicity',
  languages: 'Languages',
  religion: 'Religion',
  residentStatus: 'Resident status',
  smoking: 'Smoking',
  drinking: 'Drinking',
  drug: 'Drugs',
  summary: 'Bio',
};

export interface InterviewProgress {
  currentTopic: INTERVIEW_TOPIC | null;
  completedTopics: INTERVIEW_TOPIC[];
}

export interface S3FileCatalog {
  id: string;
  url: string;
}

export interface PhotoEntity {
  id: string;
  userId: string;
  sImage?: S3FileCatalog;
  mImage?: S3FileCatalog;
  lImage?: S3FileCatalog;
  type: number;
  createdAt: string;
  updatedAt: string;
}

export const ONBOARDING_STEP_LABEL: Record<OnboardingStep, string> = {
  [OnboardingStep.START]: 'Start',
  [OnboardingStep.READY_FOR_INTERVIEW]: 'Ready for Interview',
  [OnboardingStep.INTERVIEW]: 'Interview',
  [OnboardingStep.UPLOAD_PHOTO]: 'Upload Photo',
  [OnboardingStep.PLAN_COMPARISON]: 'Plan Comparison',
  [OnboardingStep.SUBSCRIPTION]: 'Subscription',
  [OnboardingStep.REVIEW_PROFILE]: 'Review Profile',
  [OnboardingStep.COMPLETED]: 'Completed',
};

export interface UserEntity {
  id: string;
  email: string;
  emailVerified: boolean;
  adminRole?: AdminRole;
  accountStatus?: AccountStatus;
  membershipType?: MembershipType;
  userType?: UserType;
  /** App language setting ('en' or 'zh-CN'). */
  language?: string;
  lastRequestAt: string;
  createdAt: string;
  deletedAt?: string;
  profile: any;
  photos?: PhotoEntity[];
  interviewStage: number; // 0: not started, -1 completed
  onboardingStep: OnboardingStep;
  interviewProgress: InterviewProgress;
}

export interface PublicProfileResponse {
  success: boolean;
  statusCode?: string;
  message?: string;
  data?: { user: UserEntity };
}

export interface FetchManyUsersResponse {
  data: UserEntity[];
  // Server returns `meta` (see server util.ts recordsToResponse), not `pagination`.
  meta: { hasMore: boolean; total?: number };
}

export interface FetchManyUsersDto extends Pagination {
  limit?: number;
  search?: string;
  // Comma-separated values; see UserFilters.tsx + server findUsersDto.
  gender?: string;
  onboardingStep?: string;
  accountStatus?: string;
  membershipType?: string;
  userType?: string;
  interviewTopic?: string;
  ageMin?: string;
  ageMax?: string;
  includeDeleted?: boolean;
}

export interface UserQrCodeDto {
  token: string;
  payload: string;
}

export interface QrPersonCard {
  userId: string;
  name: string;
  age?: number;
  role?: string;
  location?: string;
  photoUrl?: string;
}

export type QrMatchRequestStatus =
  | 'pending'
  | 'accepted'
  | 'declined'
  | 'cancelled'
  | 'expired';

export interface QrPairResetSummary {
  requests: number;
  suggestions: number;
  conversations: number;
  userConversations: number;
}

export interface QrMatchRequestDto {
  id: string;
  status: QrMatchRequestStatus;
  requester: QrPersonCard;
  owner: QrPersonCard;
  createdAt: string;
  respondedAt?: string | null;
  expiresAt: string;
}

// Define a service using a base URL and expected endpoints
export const userApi = api.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<UserEntity, void>({
      query: () => ({
        url: '/users/login',
        method: 'POST',
      }),
    }),
    adminUpdateUser: builder.mutation<UserEntity, Partial<UserEntity>>({
      query: (user) => ({
        url: `/users/admin/${user.id}`,
        method: 'PATCH',
        body: user,
      }),
    }),
    adminUpdateHiddenProfileFields: builder.mutation<
      UserEntity,
      { id: string; hiddenFields: HideableProfileField[] }
    >({
      query: ({ id, hiddenFields }) => ({
        url: `/users/admin/${id}/hidden-fields`,
        method: 'PATCH',
        body: { hiddenFields },
      }),
    }),
    fetchManyUsers: builder.query<FetchManyUsersResponse, FetchManyUsersDto>({
      query: (params) => ({
        url: '/users',
        method: 'GET',
        params,
      }),
      providesTags: ['Users'],
    }),
    fetchUserProfile: builder.query<UserEntity, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'GET',
      }),
      providesTags: (_r, _e, id) => [{ type: 'UserProfile', id }],
    }),
    fetchUserPublicProfile: builder.query<PublicProfileResponse, string>({
      query: (id) => ({
        url: `/users/admin/${id}/public-profile`,
        method: 'GET',
      }),
    }),
    adminDeleteUser: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/users/admin/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),
    downloadUserProfiles: builder.mutation<any, string[]>({
      query: (userIds) => ({
        url: '/users/download/profiles',
        method: 'POST',
        body: { userIds },
      }),
    }),
    importUserProfiles: builder.mutation<
      {
        created: { originalEmail: string; newEmail: string; id: string }[];
        skipped: { originalEmail: string; reason: string }[];
      },
      any[]
    >({
      query: (users) => ({
        url: '/users/admin/import-profiles',
        method: 'POST',
        body: { users },
      }),
    }),
    importSampleProfiles: builder.mutation<
      {
        created: {
          user_id: string;
          id: string;
          email: string;
          warnings: string[];
          photosUploaded: number;
          photosMissing: number;
        }[];
        skipped: { user_id: string; reason: string }[];
      },
      FormData
    >({
      query: (formData) => ({
        url: '/users/admin/import-sample-profiles',
        method: 'POST',
        body: formData,
      }),
    }),
    fetchUserQrCode: builder.query<UserQrCodeDto, string>({
      query: (id) => ({ url: `/qr/users/${id}`, method: 'GET' }),
      providesTags: (_r, _e, id) => [{ type: 'UserProfile', id: `${id}-qr` }],
    }),
    fetchUserQrRequests: builder.query<QrMatchRequestDto[], string>({
      query: (id) => ({
        url: `/qr/users/${id}/requests`,
        method: 'GET',
        params: { direction: 'incoming' },
      }),
      providesTags: (_r, _e, id) => [
        { type: 'UserProfile', id: `${id}-qr-requests` },
      ],
    }),
    respondUserQrRequest: builder.mutation<
      QrMatchRequestDto,
      { userId: string; requestId: string; action: 'accept' | 'decline' }
    >({
      query: ({ userId, requestId, action }) => ({
        url: `/qr/users/${userId}/requests/${requestId}/respond`,
        method: 'POST',
        body: { action },
      }),
      invalidatesTags: (_r, _e, { userId }) => [
        { type: 'UserProfile', id: `${userId}-qr-requests` },
      ],
    }),
    resetQrPair: builder.mutation<
      QrPairResetSummary,
      { userId: string; otherUserId: string }
    >({
      query: ({ userId, otherUserId }) => ({
        url: `/qr/users/${userId}/pair/${otherUserId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { userId, otherUserId }) => [
        { type: 'UserProfile', id: `${userId}-qr-requests` },
        { type: 'UserProfile', id: `${otherUserId}-qr-requests` },
        'UserSuggestions',
        'UserMatches',
      ],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLoginMutation,
  useAdminUpdateUserMutation,
  useAdminUpdateHiddenProfileFieldsMutation,
  useFetchManyUsersQuery,
  useLazyFetchManyUsersQuery,
  useFetchUserProfileQuery,
  useLazyFetchUserProfileQuery,
  useFetchUserPublicProfileQuery,
  useAdminDeleteUserMutation,
  useDownloadUserProfilesMutation,
  useImportUserProfilesMutation,
  useImportSampleProfilesMutation,
  useFetchUserQrCodeQuery,
  useFetchUserQrRequestsQuery,
  useRespondUserQrRequestMutation,
  useResetQrPairMutation,
} = userApi;
